import { useCallback, useEffect, useState } from "react";
import useEscapeKey from "./useEscapeKey";

const NO_DRAWINGS = { drawings: [], selectedId: null };
const NO_PALETTE = [];

const ESCAPE_OWNER_SELECTOR = '.futures-chart .popover__panel, .futures-chart [aria-modal="true"]';

/**
 * Drawings of the user. The engine keeps them, the hook follows
 * engine.events.onDrawingsChange. Position lines are a layer of their own in
 * the engine, so `drawings` (the counter, «Delete all») never contains them.
 * Escape removes the selection.
 *
 * @param {object|null} engine - ChartEngine, null until it is created
 * @returns {{ drawings: object[], selectedId: string|null,
 *   palette: string[], add: Function, remove: Function, removeAll: Function,
 *   setStyle: Function, update: Function, setLocked: Function, clone: Function }} drawings: engine.getDrawings() items;
 *   palette: engine.getLinePalette()
 */
const useDrawings = (engine) => {
  const [state, setState] = useState(NO_DRAWINGS);

  useEffect(() => {
    if (!engine) return undefined;

    const unsubscribe = engine.events.onDrawingsChange(setState);

    return () => {
      unsubscribe();
      setState(NO_DRAWINGS);
    };
  }, [engine]);

  const add = useCallback((toolId) => engine?.addDrawing(toolId), [engine]);

  const remove = useCallback((id) => engine?.removeDrawing(id), [engine]);

  const removeAll = useCallback(() => engine?.removeAllDrawings(), [engine]);

  const setStyle = useCallback((id, change) => engine?.setDrawingStyle(id, change), [engine]);

  const update = useCallback((id, settings) => engine?.updateDrawing(id, settings), [engine]);

  const setLocked = useCallback((id, isLocked) => engine?.setDrawingLocked(id, isLocked), [engine]);

  const clone = useCallback((id) => engine?.cloneDrawing(id), [engine]);

  // An open menu or modal takes Escape, the selection stays
  const handleEscape = useCallback(() => {
    if (!document.querySelector(ESCAPE_OWNER_SELECTOR)) engine?.selectDrawing(null);
  }, [engine]);

  useEscapeKey(handleEscape);

  return {
    ...state,
    palette: engine ? engine.getLinePalette() : NO_PALETTE,
    add,
    remove,
    removeAll,
    setStyle,
    update,
    setLocked,
    clone,
  };
};

export default useDrawings;
